// Brand v5.3 copy changes layered over the recovered live-site locale files.
const alts = (list) => Object.fromEntries(list.map((alt, i) => [i, { alt }]));
const values = (chapter, heading, items) => ({
  chapter,
  heading,
  items: items.map(([title, text]) => ({ title, text })),
});

const OVERRIDES = {
  en: {
    aboutPage: {
      values: values("Our Values", "Four values guide how Stige works.", [
        ["Trust", "Clear commitments, credible relationships and evidence-led communication."],
        ["Precision", "Structured thinking, careful detail and disciplined execution."],
        ["Connection", "Bringing together people, products, capabilities and opportunities."],
        ["Progress", "A forward-looking identity that can evolve with new markets, services and models."],
      ]),
      s2: { chapter: "How We Work With Buyers", heading: "Operating principles behind every enquiry.", principles: { 2: { title: "Regulatory Awareness" } } },
    },
    categories: {
      items: alts([
        "Pharmaceutical dosage forms arranged in silver blister packaging",
        "Nutraceutical softgels, capsules and powdered formulation",
        "Transparent medical device connectors and tubing",
        "Layered sterile medical consumable materials and fibres",
        "Botanical material, herbal extract and finished capsules",
        "Ayurvedic raw materials, resin, oil and powdered formulation",
      ]),
    },
    form: {
      successTextNoAck:
        "Thank you, {{name}}. Your enquiry has been recorded. Our team will review your requirement based on product specifications, commercial requirements and destination-market requirements.",
    },
  },
  ar: {
    aboutPage: {
      values: values("قيمنا", "أربع قيم توجّه طريقة عمل Stige.", [
        ["الثقة", "التزامات واضحة وعلاقات موثوقة وتواصل قائم على الأدلة."],
        ["الدقة", "تفكير منظم وعناية بالتفاصيل وتنفيذ منضبط."],
        ["التواصل", "الجمع بين الأشخاص والمنتجات والقدرات والفرص."],
        ["التقدّم", "هوية متطلعة إلى المستقبل تتطور مع الأسواق والخدمات والنماذج الجديدة."],
      ]),
      s2: { chapter: "كيف نعمل مع المشترين", heading: "مبادئ تشغيلية ترافق كل استفسار.", principles: { 2: { title: "الوعي التنظيمي" } } },
    },
    categories: {
      items: alts([
        "أشكال دوائية صيدلانية مرتبة في عبوات بليستر فضية",
        "كبسولات هلامية وكبسولات ومستحضر مسحوق من المكملات الغذائية",
        "موصلات وأنابيب شفافة لأجهزة طبية",
        "طبقات وألياف من مواد مستهلكات طبية معقمة",
        "مادة نباتية ومستخلص عشبي وكبسولات جاهزة",
        "مواد خام أيورفيدية: راتنج وزيت ومستحضر مسحوق",
      ]),
    },
    form: {
      successTextNoAck: "شكراً لك، {{name}}. تم تسجيل استفسارك. سيراجع فريقنا متطلباتك بناءً على مواصفات المنتج والمتطلبات التجارية ومتطلبات السوق المستهدفة.",
    },
  },
  fr: {
    aboutPage: {
      values: values("Nos valeurs", "Quatre valeurs guident la manière dont Stige travaille.", [
        ["Confiance", "Des engagements clairs, des relations crédibles et une communication fondée sur des faits."],
        ["Précision", "Une réflexion structurée, un soin du détail et une exécution rigoureuse."],
        ["Connexion", "Rapprocher les personnes, les produits, les capacités et les opportunités."],
        ["Progrès", "Une identité tournée vers l'avenir, capable d'évoluer avec de nouveaux marchés, services et modèles."],
      ]),
      s2: { chapter: "Notre façon de travailler avec les acheteurs", heading: "Les principes opérationnels derrière chaque demande.", principles: { 2: { title: "Sensibilité réglementaire" } } },
    },
    categories: {
      items: alts([
        "Formes pharmaceutiques disposées dans des blisters argentés",
        "Capsules molles, gélules et formulation en poudre de nutraceutiques",
        "Connecteurs et tubulures transparents de dispositifs médicaux",
        "Matériaux et fibres de consommables médicaux stériles en couches",
        "Matière botanique, extrait de plante et gélules finies",
        "Matières premières ayurvédiques : résine, huile et formulation en poudre",
      ]),
    },
    form: {
      successTextNoAck:
        "Merci, {{name}}. Votre demande a bien été enregistrée. Notre équipe examinera votre besoin au regard des spécifications produit, des exigences commerciales et des exigences du marché de destination.",
    },
  },
  es: {
    aboutPage: {
      values: values("Nuestros valores", "Cuatro valores guían la forma de trabajar de Stige.", [
        ["Confianza", "Compromisos claros, relaciones creíbles y una comunicación basada en evidencias."],
        ["Precisión", "Pensamiento estructurado, atención al detalle y ejecución disciplinada."],
        ["Conexión", "Unir personas, productos, capacidades y oportunidades."],
        ["Progreso", "Una identidad orientada al futuro, capaz de evolucionar con nuevos mercados, servicios y modelos."],
      ]),
      s2: { chapter: "Cómo trabajamos con los compradores", heading: "Principios operativos detrás de cada consulta.", principles: { 2: { title: "Conciencia regulatoria" } } },
    },
    categories: {
      items: alts([
        "Formas farmacéuticas dispuestas en blísteres plateados",
        "Cápsulas blandas, cápsulas y formulación en polvo de nutracéuticos",
        "Conectores y tubos transparentes de dispositivos médicos",
        "Capas y fibras de materiales consumibles médicos estériles",
        "Material botánico, extracto herbal y cápsulas terminadas",
        "Materias primas ayurvédicas: resina, aceite y formulación en polvo",
      ]),
    },
    form: {
      successTextNoAck:
        "Gracias, {{name}}. Hemos registrado su consulta. Nuestro equipo revisará su requerimiento según las especificaciones del producto, los requisitos comerciales y los requisitos del mercado de destino.",
    },
  },
  pt: {
    home: { hero: { meta: { 2: "Aprovisionamento" } } },
    aboutPage: {
      values: values("Os nossos valores", "Quatro valores orientam a forma como a Stige trabalha.", [
        ["Confiança", "Compromissos claros, relações credíveis e comunicação baseada em evidências."],
        ["Precisão", "Pensamento estruturado, atenção ao detalhe e execução disciplinada."],
        ["Ligação", "Aproximar pessoas, produtos, capacidades e oportunidades."],
        ["Progresso", "Uma identidade virada para o futuro, capaz de evoluir com novos mercados, serviços e modelos."],
      ]),
      s2: { chapter: "Como trabalhamos com os compradores", heading: "Princípios operacionais por detrás de cada pedido.", principles: { 2: { title: "Consciência regulamentar" } } },
    },
    categories: {
      items: alts([
        "Formas farmacêuticas dispostas em blisters prateados",
        "Cápsulas moles, cápsulas e formulação em pó de nutracêuticos",
        "Conectores e tubos transparentes de dispositivos médicos",
        "Camadas e fibras de materiais consumíveis médicos esterilizados",
        "Material botânico, extrato de plantas e cápsulas acabadas",
        "Matérias-primas ayurvédicas: resina, óleo e formulação em pó",
      ]),
    },
    form: {
      successTextNoAck:
        "Obrigado, {{name}}. O seu pedido foi registado. A nossa equipa irá analisar o seu requisito com base nas especificações do produto, nos requisitos comerciais e nos requisitos do mercado de destino.",
    },
  },
  vi: {
    aboutPage: {
      values: values("Giá trị của chúng tôi", "Bốn giá trị định hướng cách Stige làm việc.", [
        ["Tin cậy", "Cam kết rõ ràng, quan hệ đáng tin cậy và giao tiếp dựa trên bằng chứng."],
        ["Chính xác", "Tư duy có cấu trúc, chú trọng chi tiết và thực thi kỷ luật."],
        ["Kết nối", "Kết nối con người, sản phẩm, năng lực và cơ hội."],
        ["Tiến bộ", "Bản sắc hướng tới tương lai, có thể phát triển cùng các thị trường, dịch vụ và mô hình mới."],
      ]),
      s2: { chapter: "Cách chúng tôi làm việc với người mua", heading: "Các nguyên tắc vận hành trong mọi yêu cầu.", principles: { 2: { title: "Am hiểu quy định" } } },
    },
    categories: {
      items: alts([
        "Các dạng bào chế dược phẩm xếp trong vỉ thuốc màu bạc",
        "Viên nang mềm, viên nang và dạng bột của thực phẩm chức năng",
        "Đầu nối và ống dẫn trong suốt của thiết bị y tế",
        "Các lớp vật liệu và sợi của vật tư y tế vô trùng",
        "Nguyên liệu thực vật, chiết xuất thảo dược và viên nang thành phẩm",
        "Nguyên liệu Ayurveda: nhựa cây, dầu và dạng bột",
      ]),
    },
    form: {
      successTextNoAck:
        "Cảm ơn {{name}}. Yêu cầu của bạn đã được ghi nhận. Đội ngũ của chúng tôi sẽ xem xét yêu cầu dựa trên thông số sản phẩm, yêu cầu thương mại và yêu cầu của thị trường đích.",
    },
  },
  id: {
    aboutPage: {
      values: values("Nilai Kami", "Empat nilai yang memandu cara Stige bekerja.", [
        ["Kepercayaan", "Komitmen yang jelas, hubungan yang kredibel, dan komunikasi berbasis bukti."],
        ["Presisi", "Pemikiran terstruktur, ketelitian, dan pelaksanaan yang disiplin."],
        ["Koneksi", "Menghubungkan orang, produk, kapabilitas, dan peluang."],
        ["Kemajuan", "Identitas yang berorientasi ke depan dan dapat berkembang bersama pasar, layanan, dan model baru."],
      ]),
      s2: { chapter: "Cara Kami Bekerja dengan Pembeli", heading: "Prinsip operasional di balik setiap permintaan.", principles: { 2: { title: "Kesadaran Regulasi" } } },
    },
    categories: {
      items: alts([
        "Bentuk sediaan farmasi tersusun dalam kemasan blister perak",
        "Kapsul lunak, kapsul, dan formulasi bubuk nutrasetikal",
        "Konektor dan selang transparan perangkat medis",
        "Lapisan dan serat bahan habis pakai medis steril",
        "Bahan botani, ekstrak herbal, dan kapsul jadi",
        "Bahan baku Ayurveda: resin, minyak, dan formulasi bubuk",
      ]),
    },
    form: {
      successTextNoAck:
        "Terima kasih, {{name}}. Permintaan Anda telah tercatat. Tim kami akan meninjau kebutuhan Anda berdasarkan spesifikasi produk, persyaratan komersial, dan persyaratan pasar tujuan.",
    },
  },
  ms: {
    aboutPage: {
      values: values("Nilai Kami", "Empat nilai membimbing cara Stige bekerja.", [
        ["Kepercayaan", "Komitmen yang jelas, hubungan yang boleh dipercayai dan komunikasi berasaskan bukti."],
        ["Ketepatan", "Pemikiran berstruktur, perhatian terhadap butiran dan pelaksanaan yang berdisiplin."],
        ["Hubungan", "Menghubungkan orang, produk, keupayaan dan peluang."],
        ["Kemajuan", "Identiti berpandangan ke hadapan yang boleh berkembang bersama pasaran, perkhidmatan dan model baharu."],
      ]),
      s2: { chapter: "Cara Kami Bekerja dengan Pembeli", heading: "Prinsip operasi di sebalik setiap pertanyaan.", principles: { 2: { title: "Kesedaran Kawal Selia" } } },
    },
    categories: {
      items: alts([
        "Bentuk dos farmaseutikal tersusun dalam pek lepuh perak",
        "Kapsul lembut, kapsul dan formulasi serbuk nutraseutikal",
        "Penyambung dan tiub lutsinar peranti perubatan",
        "Lapisan dan gentian bahan pakai buang perubatan steril",
        "Bahan botani, ekstrak herba dan kapsul siap",
        "Bahan mentah Ayurveda: resin, minyak dan formulasi serbuk",
      ]),
    },
    form: {
      successTextNoAck:
        "Terima kasih, {{name}}. Pertanyaan anda telah direkodkan. Pasukan kami akan menyemak keperluan anda berdasarkan spesifikasi produk, keperluan komersial dan keperluan pasaran destinasi.",
    },
  },
};

export default OVERRIDES;
